import React, { useState, useMemo } from 'react';
import { Ruler } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { HeightInput, WeightInput, LengthInput } from '../../components/calculators/MeasureInputs';
import { GenderField } from '../../components/calculators/FormFields';
import UnitToggle from '../../components/calculators/UnitToggle';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculateBodyFat, getBodyFatCategory, calculateLBM } from '../../lib/calculatorUtils';

const BodyFatCalculatorPage = () => {
  const [hwUnit, setHwUnit] = useState('metric');   // for height/weight
  const [bodyUnit, setBodyUnit] = useState('metric'); // for waist/neck/hip
  const [gender, setGender] = useState('female');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [waistCm, setWaistCm] = useState('');
  const [neckCm, setNeckCm] = useState('');
  const [hipCm, setHipCm] = useState('');

  const result = useMemo(() => {
    const h = parseFloat(heightCm);
    const w = parseFloat(weightKg);
    const waist = parseFloat(waistCm);
    const neck = parseFloat(neckCm);
    const hip = gender === 'female' ? parseFloat(hipCm) : null;
    if (!h || !w || !waist || !neck) return null;
    if (gender === 'female' && !hip) return null;
    const bf = calculateBodyFat({ gender, heightCm: h, waistCm: waist, neckCm: neck, hipCm: hip });
    if (!bf || bf <= 0 || bf > 70) return { invalid: true };
    const implausible = (gender === 'male' && bf > 40) || (gender === 'female' && bf > 50);
    return {
      bf,
      category: getBodyFatCategory(bf, gender),
      lbm: calculateLBM(w, bf),
      fatMass: +(w * (bf / 100)).toFixed(1),
      implausible
    };
  }, [gender, heightCm, weightKg, waistCm, neckCm, hipCm]);

  const inputs = (
    <div className="space-y-5" data-testid="bf-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Body Measurements</h2>
      <p className="text-slate-400 text-sm mb-5">Uses the U.S. Navy body fat formula.</p>
      <GenderField value={gender} onChange={setGender} testIdPrefix="bf-gender" />
      <HeightInput unit={hwUnit} onUnitChange={setHwUnit} heightCm={heightCm} onHeightCmChange={setHeightCm} />
      <WeightInput unit={hwUnit} onUnitChange={setHwUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />

      <div className="pt-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-300">Measurements</span>
          <UnitToggle
            value={bodyUnit}
            onChange={setBodyUnit}
            options={[{ value: 'metric', label: 'cm' }, { value: 'imperial', label: 'in' }]}
            label="Measurement unit"
            testIdPrefix="bf-meas-unit"
          />
        </div>
        <div className={`grid ${gender === 'female' ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'} gap-3`}>
          <LengthInput id="bf-waist" label="Waist" unit={bodyUnit} valueCm={waistCm} onValueCmChange={setWaistCm} testId="bf-waist-input" />
          <LengthInput id="bf-neck" label="Neck" unit={bodyUnit} valueCm={neckCm} onValueCmChange={setNeckCm} testId="bf-neck-input" />
          {gender === 'female' && (
            <LengthInput id="bf-hip" label="Hip" unit={bodyUnit} valueCm={hipCm} onValueCmChange={setHipCm} testId="bf-hip-input" />
          )}
        </div>
        <p className="text-slate-500 text-xs mt-2">Measure waist at navel, neck below larynx, and (women) hip at widest point.</p>
      </div>
    </div>
  );

  const resultNode = result?.bf ? (
    <div className="flex flex-col h-full">
      <PrimaryResult
        label="Estimated Body Fat"
        value={result.bf}
        unit="%"
        accent="from-violet-500 to-fuchsia-500"
        category={result.category}
        testId="bf-result-percent"
      />
      <div className="mt-8 space-y-1">
        <StatRow label="Lean Body Mass" value={result.lbm} unit="kg" color="text-emerald-300" testId="bf-lbm" />
        <StatRow label="Fat Mass" value={result.fatMass} unit="kg" color="text-rose-300" testId="bf-fat-mass" />
        <StatRow label="Category" value={result.category.label} color={result.category.color} testId="bf-category" />
      </div>
      {result.implausible && (
        <div className="mt-5 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-amber-200 text-xs sm:text-sm" data-testid="bf-implausible-warning">
          This result is outside the typical biological range. Please double-check your measurements (especially the neck) and try again.
        </div>
      )}
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        The U.S. Navy method is an estimation. For clinical accuracy, DEXA, hydrostatic weighing or BodPod assessments are preferred.
      </p>
    </div>
  ) : result?.invalid ? (
    <div className="flex flex-col h-full justify-center text-center py-10">
      <p className="text-rose-400 font-semibold mb-2">Result out of range</p>
      <p className="text-slate-400 text-sm max-w-sm mx-auto">Please check your measurements. Waist must be larger than neck (men) or waist + hip must exceed neck (women).</p>
    </div>
  ) : (
    <EmptyResult message={`Enter your measurements${gender === 'female' ? ' (waist, neck, hip)' : ' (waist, neck)'} to estimate body fat %.`} />
  );

  return (
    <CalculatorLayout
      slug="body-fat"
      title="Body Fat Percentage Calculator"
      subtitle="Estimate your body fat percentage and lean body mass using the U.S. Navy formula."
      metaDescription="Body Fat Calculator from TCUBE FITNESS to estimate body fat %, lean body mass and fat mass using the U.S. Navy method."
      icon={Ruler}
      accentGradient="from-violet-500 to-fuchsia-500"
      accentColor="text-violet-400"
      badgeBg="bg-violet-500/10"
      badgeBorder="border-violet-500/30"
      inputs={inputs}
      result={resultNode}
      formulaText={
        <>
          <p><strong className="text-slate-300">U.S. Navy Body Fat Formula:</strong></p>
          <p>Male: BF% = 86.010 × log₁₀(waist − neck) − 70.041 × log₁₀(height) + 36.76</p>
          <p>Female: BF% = 163.205 × log₁₀(waist + hip − neck) − 97.684 × log₁₀(height) − 78.387</p>
          <p><strong className="text-slate-300">Lean Body Mass:</strong> LBM = weight × (1 − body fat % / 100)</p>
        </>
      }
    />
  );
};

export default BodyFatCalculatorPage;
